import type { TopUpOption } from "@/components/staff/payment/modal/TopUpModal/TopUpForm";

import { Pagination } from "@/@types/general";
import { Subscription } from "@/@types/subscriptions";
import { Profile } from "@/api/auth/auth.types.ts";
import { authAxios, baseAxios, billingAxios } from "@/api/axios.ts";
import {
  CreateProfile,
  CreditCardModel,
  LoginCodeModel,
  MovieOrderModel,
  NotificationDetail,
  NotificationsCount,
  NotificationsList,
  OrderModel,
  OrderParams,
  OrderType,
  PatchProfile,
  ProfilePicturesCategory,
  SessionModel,
  UserModel,
} from "@/api/user/user.types.ts";

class UserRequests {
  static isLoggedIn() {
    return Boolean(localStorage.getItem("splay:token"));
  }
  static async getLoginCode() {
    return authAxios<{ data: { code: number } }>("/user/code-login-get").then((res) => res.data);
  }

  static async getProfileDetail(id: number) {
    const token = localStorage.getItem("splay:token") as string;
    return baseAxios<Profile>(`/user/profiles/${id}`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    }).then((res) => res.data);
  }

  static async getProfilePicturesCategory() {
    const token = localStorage.getItem("splay:token") as string;
    return authAxios<ProfilePicturesCategory[]>({
      url: "/user/profile-pictures-category",
      method: "GET",
      headers: {
        Authorization: `Token ${token}`,
      },
    }).then((res) => res.data);
  }
  static async createPostProfile(data: CreateProfile) {
    const formData = new FormData();
    formData.append("name", data.name);
    console.log(data);
    if (data.gender) formData.append("gender", data.gender);
    if (data.date_of_birth) formData.append("date_of_birth", data.date_of_birth);
    if (data.picture_id) formData.append("picture_id", data.picture_id.toString());
    if (data.pin_code) formData.append("pin_code", data.pin_code);
    if (data.interface_lang) formData.append("interface_lang", data.interface_lang);
    if (data.content_all) formData.append("content_all", data.content_all.toString());
    if (data.content_ru) formData.append("content_ru", data.content_ru.toString());
    if (data.content_uz) formData.append("content_uz", data.content_uz.toString());
    if (data.selected_content) {
      data.selected_content.forEach((item) => {
        formData.append("selected_content[]", item);
      });
    }

    const token = localStorage.getItem("splay:token") as string;

    return baseAxios({
      url: "/user/profiles",
      method: "POST",
      data: formData,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Token ${token}`,
      },
    });
  }

  static async destroyProfile(id: number) {
    const token = localStorage.getItem("splay:token") as string;
    return baseAxios({
      url: `/user/profiles/${id}`,
      method: "DELETE",
      headers: {
        Authorization: `Token ${token}`,
      },
    });
  }

  static async patchProfile(user_id: number, data: PatchProfile) {
    const formData = new FormData();
    if (data.name) formData.append("name", data.name);
    if (data.gender) formData.append("gender", data.gender);
    if (data.pin_code !== undefined) {
      formData.append("pin_code", data.pin_code);
    }
    if (data.content_all) {
      formData.append("content_all", data.content_all.toString());
    } else {
      if (data.content_ru) {
        formData.append("content_ru", data.content_ru.toString());
        formData.append("content_uz", "false");
        formData.append("content_all", "false");
      }

      if (data.content_uz) {
        formData.append("content_uz", data.content_uz.toString());
        formData.append("content_ru", "false");
        formData.append("content_all", "false");
      }
    }

    if (data.interface_lang) formData.append("interface_lang", data.interface_lang);
    if (data.date_of_birth) formData.append("date_of_birth", data.date_of_birth);
    if (data.picture_id) formData.append("picture_id", data.picture_id.toString());
    if (data.selected_content && data.selected_content.length) {
      data.selected_content.forEach((item) => {
        formData.append("selected_content[]", item);
      });
    }
    const token = localStorage.getItem("splay:token") as string;
    return baseAxios({
      url: `/user/profiles/${user_id}`,
      method: "PATCH",
      data: formData,
      headers: {
        Authorization: `Token ${token}`,
      },
    }).then((res) => res.data);
  }

  static async postCheckPinCode({ formData }: { formData: FormData }) {
    const token = localStorage.getItem("splay:token") as string;
    return authAxios.post("/user/profiles-check-pin-code", formData, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });
  }

  static async postChangePassword({ formData }: { formData: FormData }) {
    return authAxios.post("/user/password-change-while-logged-in", formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("splay:access")}`,
      },
    });
  }

  static async postResetPassword({ formData }: { formData: FormData }) {
    const access = localStorage.getItem("splay:access");

    return authAxios.post("/user/reset-password", formData, {
      headers: access
        ? {
            Authorization: `Bearer ${localStorage.getItem("splay:access")}`,
          }
        : undefined,
    });
  }

  static async putCreditCard({ formData, id }: { formData: FormData; id: number }) {
    return authAxios.put(`/user/bank-cards/${id}`, formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("splay:access")}`,
      },
    });
  }

  static async deleteCreditCard({ id }: { id: number }) {
    return authAxios.delete(`/user/bank-cards/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("splay:access")}`,
      },
    });
  }

  static async deleteAccountSendCode() {
    return authAxios.post(`/user/account-delete-send-code`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("splay:access")}`,
      },
    });
  }

  static async deleteAccount(code: string) {
    return authAxios.post(
      `/user/account-delete`,
      { code },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("splay:access")}`,
        },
      },
    );
  }

  static async addCreditCardInit({ ...params }: { card_number: string; expire_date: string }) {
    return billingAxios.post(
      `/billing/atmos/bind-card/init`,
      { ...params },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("splay:access")}`,
        },
      },
    );
  }

  static async addCreditCardConfirm({ ...params }: { transaction_id: string; otp: string }) {
    return billingAxios.post(
      `/billing/atmos/bind-card/confirm`,
      { ...params },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("splay:access")}`,
        },
      },
    );
  }

  static async loginCodeConfirm(code: string) {
    return authAxios.post(
      `/user/code-login-confirm`,
      { code },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("splay:access")}`,
        },
      },
    );
  }

  static async deleteSession(my_key: string) {
    return authAxios.post(
      `/user/session-delete`,
      { my_key },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("splay:access")}`,
        },
      },
    );
  }

  static async deleteAllSessions() {
    return authAxios.post(`/user/logout-all-except-active`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("splay:access")}`,
      },
    });
  }

  static async postLogout() {
    const access = localStorage.getItem("splay:access");
    if (!access) return Promise.reject("No access token found");
    const formData = new FormData();
    formData.append("access", access);
    return authAxios.post("/user/logout", formData);
  }

  static async getNotifications(limit: number = 25, offset: number = 0) {
    const searchParams = new URLSearchParams();
    if (limit !== undefined) searchParams.append("limit", limit.toString());
    if (offset !== undefined) searchParams.append("offset", offset.toString());

    return authAxios<NotificationsList>({
      url: "/user/notifications",
      method: "GET",
      params: searchParams,
    }).then((res) => res.data);
  }

  static async getNotificationsCount() {
    return authAxios<NotificationsCount>("/user/notifications-unread-count").then((res) => res.data);
  }

  static async getNotificationDetail(id: number) {
    return authAxios<NotificationDetail>(`/user/notifications/${id}`).then((res) => res.data);
  }

  static async getUserDetail() {
    return authAxios<UserModel>(`/user`).then((res) => res.data);
  }

  static async getCreditCards() {
    return authAxios<CreditCardModel[]>(`/user/bank-cards`).then((res) => res.data);
  }

  static async getSubscriptions() {
    return authAxios<Subscription[]>(`/user/subscriptions`).then((res) => res.data);
  }

  static async getSessions() {
    return authAxios<SessionModel[]>(`/user/session-list`).then((res) => res.data);
  }

  static async getMovieOrders() {
    return authAxios<Pagination<MovieOrderModel>>(`/user/content-order`).then((res) => res.data);
  }

  static async getOrders({ orderType }: { orderType?: OrderType }) {
    return authAxios<Pagination<OrderModel>>(`/user/orders`, {
      params: { order_type: orderType != "all" ? orderType : null },
    }).then((res) => res.data);
  }

  static async createLoginCode() {
    return authAxios<LoginCodeModel>(`/user/code-login-create`).then((res) => res.data);
  }

  static async getOrder(id: number) {
    return authAxios<OrderModel>(`/user/orders/${id}`).then((res) => res.data);
  }

  static async getPaymentMethods(id: number) {
    return billingAxios<TopUpOption[]>(`/billing/available-payment-methods/`, { params: { tr_id: id } }).then(
      (res) => res.data,
    );
  }

  static async createOrder({ ...params }: OrderParams) {
    return authAxios.post<OrderModel>(`/user/orders`, params, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("splay:access")}`,
      },
    });
  }

  static async performOrder({
    ...params
  }: {
    tr_id: number;
    payment_type: "balance" | "bank_card";
    bank_card_id?: number;
  }) {
    return billingAxios.post(
      `/billing/perform-order-from/`,
      {},
      {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("splay:access")}`,
        },
      },
    );
  }

  static async performBonusPayment({ ...params }: { bank_card_id: number }) {
    return billingAxios.post(`/billing/perform-bonus-payment/`, undefined, {
      params,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("splay:access")}`,
      },
    });
  }

  static async createOrderAndPerform({ ...params }: OrderParams) {
    try {
      const { data: order } = await this.createOrder(params);

      const isFromBalance = params.bank_card_id == "balance";

      if (order) {
        await this.performOrder({
          tr_id: order.id,
          payment_type: isFromBalance ? "balance" : "bank_card",
          bank_card_id: isFromBalance ? undefined : (params.bank_card_id as number),
        });

        order.status = "performed";

        return order;
      }

      throw "Order is required";
    } catch (error) {
      throw error;
    }
  }

  static async changeSubsAutoPayment({
    ...params
  }: {
    subscription_id: number;
    auto_payment: boolean;
    reason?: string;
  }) {
    return authAxios.post(`/user/subscription-auto-payment-change`, params, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("splay:access")}`,
        "Content-Type": "multipart/form-data",
      },
    });
  }

  static async orderMovie({
    ...params
  }: {
    content_name: string;
    content_description?: string;
    content_link?: string;
  }) {
    return authAxios.post(`/user/content-order`, params, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("splay:access")}`,
        "Content-Type": "multipart/form-data",
      },
    });
  }
}

export default UserRequests;
